import { useState } from "react";
import { Check, ChevronDown, ChevronUp, HelpCircle, Route, Scale } from "lucide-react";
import { api } from "@/lib/api";

const SECTIONS = [
  { key: "conflicts", title: "Worth another look…", icon: Route, tone: "text-rose-700" },
  { key: "tradeoffs", title: "You're accepting…", icon: Scale, tone: "text-emerald-800" },
  { key: "supports", title: "This choice fits because…", icon: Check, tone: "text-[#4C1D95]" },
  { key: "unresolved", title: "Still unresolved…", icon: HelpCircle, tone: "text-amber-800" },
];

const CHIPS = [
  { key: "review", cls: "border-rose-300 bg-rose-50 text-rose-800", one: "1 worth another look", many: (n) => n + " worth another look" },
  { key: "tradeoffs", cls: "border-emerald-300 bg-emerald-50 text-emerald-800", one: "1 trade-off accepted", many: (n) => n + " trade-offs accepted" },
  { key: "fits", cls: "border-violet-300 bg-violet-50 text-[#4C1D95]", one: "1 thing fits", many: (n) => n + " things fit" },
  { key: "unresolved", cls: "border-amber-300 bg-amber-50 text-amber-900", one: "1 undecided", many: (n) => n + " undecided" },
];

export function WorthItAsk({ ask, testId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");

  const answer = async (code) => {
    setSaving(code);
    setError("");
    try {
      await api.post(`/realisations/observations/${ask.observation_id}/worth-it`, { choice: code });
      onChanged?.();
    } catch {
      setError("Couldn't save that — please try again.");
    } finally {
      setSaving(null);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={testId + "-open"}
        className="mt-1 text-[10px] font-medium text-[#4C1D95] underline-offset-2 hover:underline">
        {ask.question}
      </button>
    );
  }
  return (
    <div className="mt-1.5" data-testid={testId}>
      <div className="text-[10px] text-stone-500">{ask.question} <span className="italic">Optional.</span></div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {ask.options.map((o) => (
          <button key={o.code} type="button" disabled={Boolean(saving)} onClick={() => answer(o.code)}
            data-testid={testId + "-" + o.code}
            className="rounded-full border border-stone-300 bg-white px-2 py-0.5 text-[10px] text-stone-700 transition-colors hover:border-[#4C1D95] hover:text-[#4C1D95] disabled:opacity-60">
            {saving === o.code ? "Saving…" : o.label}
          </button>
        ))}
      </div>
      {error && <div className="mt-1 text-[10px] text-rose-700" data-testid={testId + "-error"}>{error}</div>}
    </div>
  );
}

function Section({ section, items, testId, onChanged }) {
  const { key, title, icon: Icon, tone } = section;
  return (
    <div data-testid={testId + "-" + key}>
      <div className={"flex items-center gap-1.5 text-[11px] font-semibold " + tone}>
        <Icon className="h-3.5 w-3.5" />
        {title}
      </div>
      <ul className="mt-1 space-y-1 pl-5">
        {items.map((it, i) => (
          <li key={i} className="min-w-0 break-words [overflow-wrap:anywhere] text-[11px] text-stone-700" data-testid={testId + "-" + key + "-" + i}>
            {it.label}
            {it.detail && <span className="block text-[10px] text-stone-500">{it.detail}</span>}
            {it.worth_it && <WorthItAsk ask={it.worth_it} testId={testId + "-worth-it-" + i} onChanged={onChanged} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function DecisionPicture({ picture, testId, onChanged }) {
  const [open, setOpen] = useState(false);
  if (!picture) return null;
  const s = picture.summary || {};
  const counts = CHIPS.filter((c) => s[c.key]).map((c) => ({ cls: c.cls, text: s[c.key] === 1 ? c.one : c.many(s[c.key]) }));
  if (!counts.length) return null;

  return (
    <div data-testid={testId} className="mt-1.5 min-w-0 rounded-md border border-stone-200 bg-stone-50/70 px-2.5 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId + "-toggle"}
        className="flex w-full min-w-0 flex-wrap items-center gap-1.5 text-left"
      >
        <span className="text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">Your decision picture</span>
        {counts.map((c, i) => (
          <span key={i} className={"rounded-full border px-2 py-0.5 text-[11px] font-semibold " + c.cls}>{c.text}</span>
        ))}
        {open ? <ChevronUp className="ml-auto h-4 w-4 text-stone-400" /> : <ChevronDown className="ml-auto h-4 w-4 text-stone-400" />}
      </button>

      {open && (
        <div className="mt-2 space-y-2 border-t border-stone-200 pt-2" data-testid={testId + "-expanded"}>
          {SECTIONS.map((section) => {
            const items = picture[section.key] || [];
            return items.length ? <Section key={section.key} section={section} items={items} testId={testId} onChanged={onChanged} /> : null;
          })}
        </div>
      )}
    </div>
  );
}
